import React, { useEffect, useRef, useState } from 'react';
import { LoaderCircle, User } from 'lucide-react';
import { cn } from '../../lib/utils';
import { coachingAPI } from '../../services/api';
import Composer from './Composer';
import { EmptyState } from '../ds';
import { withDayDividers } from '../../v2/threadView';

/** Direct messages with a human coach. Polls while open. */
export default function HumanCoachThread({ coach, isOpen, onRead, inputRef, wide = false }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (first) => {
      if (first) setLoading(true);
      try {
        const msgs = await coachingAPI.getMessages(coach.id);
        if (!cancelled) setMessages([...msgs].reverse());
        await coachingAPI.markRead(coach.id);
        onRead?.();
      } catch {
        if (!cancelled && first) setMessages([]);
      }
      if (!cancelled && first) setLoading(false);
    };
    load(true);
    const timer = isOpen ? setInterval(() => load(false), 10000) : null;
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [coach.id, isOpen]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const msg = await coachingAPI.sendMessage(coach.id, input.trim());
      setMessages((prev) => [...prev, msg]);
      setInput('');
    } catch { /* keep the draft */ }
    setSending(false);
  };

  const rows = withDayDividers(messages);
  const time = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <div className="flex-1 overflow-y-auto">
      <div className={cn('h-full px-4 py-4', wide && 'mx-auto w-full max-w-[808px]')}>
        {loading ? (
          <div className="flex h-full flex-col items-center justify-center text-text-tertiary">
            <LoaderCircle className="mb-2 h-5 w-5 animate-spin text-accent-default" /><span className="type-body-small">Loading messages…</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="pt-24">
            <EmptyState icon={User} title={`Start a conversation with @${coach.username}`} body="Messages here go to your coach directly." />
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => {
              if (row.kind === 'day') {
                return <p key={row.key} className="m-0 text-center type-caption text-text-tertiary" role="separator" aria-label={row.label}>{row.label}</p>;
              }
              const m = row.item;
              const mine = m.sender_id !== coach.user_id;
              return (
                <div key={row.key} className={cn('flex flex-col gap-1', mine ? 'items-end' : 'items-start')}>
                  <div className={cn('max-w-[85%] px-3 py-[9px] text-[14px] leading-[1.4] text-text-primary',
                    mine ? 'rounded-2xl rounded-br-md bg-bg-raised' : 'rounded-2xl rounded-bl-md border border-border-subtle bg-bg-surface')}>
                    <p className="m-0 whitespace-pre-wrap break-words">{m.content}</p>
                  </div>
                  <p className="m-0 type-caption text-text-tertiary">{mine ? time(m.created_at) : `${coach.username} · ${time(m.created_at)}`}</p>
                </div>
              );
            })}
          </div>
        )}
        <div ref={endRef} />
      </div>
      </div>
      <Composer value={input} onChange={setInput} onSubmit={submit} disabled={sending} allowed inputRef={inputRef}
        placeholder={`Message @${coach.username}`} chip="Direct message" wide={wide} />
    </>
  );
}
